import styled from 'styled-components';
import React, { useEffect, useMemo, useState } from 'react';

export interface OffsetInputProps {
  value: number;
  onChange: (value: number) => void;
  suffix?: string;
  prefix?: string;
  step?: number;
  mode?: 'delta' | 'absolute';
}

export function OffsetInput({
  value,
  onChange,
  suffix,
  prefix,
  step = 1,
  mode = `delta`,
}: OffsetInputProps) {
  const [displayValue, setDisplayValue] = useState(`${value}`);
  const [signOverride, setSignOverride] = useState(``);

  useEffect(() => {
    if (value > 0) {
      setSignOverride(``);
    }
    setDisplayValue(`${Math.abs(value)}`);
  }, [value]);

  function handleInputChange(e) {
    const raw = e.target.value;

    const cleaned = raw.replace(/[^0-9.-]/g, ``);

    const parsed = parseFloat(cleaned);

    if (cleaned === `0-`) {
      setDisplayValue(``);
      setSignOverride(`-`);
      return;
    }

    if (cleaned === `-0`) {
      setDisplayValue(``);
      setSignOverride(`-`);
      return;
    }

    if (cleaned.endsWith(`.`)) {
      setDisplayValue(cleaned);
      return;
    }

    if (Number.isNaN(parsed)) {
      onChange?.(null);
      setSignOverride(``);
      return;
    }

    // const formatted = formatWithSign(parsed);

    setDisplayValue(`${Math.abs(parsed)}`);
    onChange?.(signOverride === `-` ? parsed * -1 : parsed);
  }

  function handleKeyDown(e) {
    if (e.key === `ArrowUp` || e.key === `ArrowDown`) {
      e.preventDefault();

      const currentParsed = parseFloat(`${value}`);
      if (Number.isNaN(currentParsed)) return;

      const delta = e.key === `ArrowUp` ? step : -step;
      const newValue = currentParsed + delta;
      onChange?.(newValue);
    }
  }

  const signedPrefix = useMemo(() => {
    if (mode === `absolute`) {
      return prefix || ``;
    }
    if (signOverride) {
      return `${signOverride}${prefix || ``}`;
    }

    if (value > 0) {
      return `+${prefix || ``}`;
    }
    if (value < 0) {
      return `-${prefix || ``}`;
    }
    return prefix || ``;
  }, [value, prefix, signOverride]);

  return (
    <Wrapper>
      <div>{signedPrefix || ``}</div>
      <input
        style={{
          width: `${Math.min(Math.max(displayValue.length, 1), 9)}ch`,
        }}
        type="text"
        value={displayValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        inputMode="decimal"
      />
      <div>{suffix || ``}</div>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  width: fit-content;
  height: auto;
  border-radius: 1rem;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.11);
  border: solid 0.5px #979797;
  background-color: #fff;
  padding: 0.8rem 1rem;
  text-align: center;

  display: grid;
  grid-template-columns: auto auto auto;
  align-content: baseline;
  justify-items: center;

  && > * {
    font-size: 1.2rem;
    color: black;
  }

  input {
    border: none;
    background: transparent;
    outline: none;
    text-align: center;
    padding: 0;
    /* Chrome, Safari, Edge, Opera */
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    /* Firefox */
    &[type='number'] {
      -moz-appearance: textfield;
    }
  }
`;
